import { Injectable, NgZone } from "@angular/core";
import { BehaviorSubject } from "rxjs";

/** A single product card in the embed shopping rail (sent by the host page over postMessage). */
export interface EmbedShopProduct {
  title: string;
  price: string;
  colors: string;
  image: string;
  url: string;
}

/** Shopping-rail payload the host page (fender.com) posts into the embed iframe. */
export interface EmbedShopContent {
  heading: string;
  blurb: string;
  shopAllUrl: string;
  products: EmbedShopProduct[];
}

/**
 * Bridge between the embeddable player (my.fender.com in an <iframe>) and its host page
 * (fender.com). The iframe cannot read the parent DOM cross-origin, so the host page posts
 * structured data in via `window.postMessage`; we validate the origin, sanitize, and expose it.
 *
 * Contract:
 *  - On start() the embed posts `{ type: "fender-embed:ready" }` to the parent so it knows when
 *    to send data (avoids a load race).
 *  - The host replies with `{ type: "fender-embed:shop", heading, blurb, shopAllUrl, products: [...] }`.
 *  - Only messages from *.fender.com / *.fendergarage.com / localhost are accepted, and only
 *    https URLs survive sanitization — we render from data, never eval or inject raw HTML.
 */
@Injectable({ providedIn: "root" })
export class EmbedBridgeService {
  private _shop$ = new BehaviorSubject<EmbedShopContent | null>(null);
  /** Latest shopping-rail content, or null when the host hasn't sent any. */
  public shop$ = this._shop$.asObservable();

  private _listener: ((e: MessageEvent) => void) | null = null;
  private _readyInterval: any = null;

  constructor(private zone: NgZone) {}

  /** Begin listening for host messages and announce readiness. Idempotent. */
  start() {
    if (this._listener || typeof window === "undefined") return;
    // Not embedded in a host page (e.g. opening /embed/… directly to check the UI). There's no
    // parent to hand-shake with, so skip the listener AND the ready pings entirely — otherwise we'd
    // ping a nonexistent parent every 800ms and spam the console. The host handshake only matters
    // inside an iframe.
    if (!window.parent || window.parent === window) {
      console.log("[embed-shop] start(): not in an iframe — skipping host handshake");
      return;
    }
    console.log("[embed-shop] start(): listening for host messages");
    this._listener = (event: MessageEvent) => {
      const data: any = event.data;
      if (!this._allowedOrigin(event.origin)) return; // ignore YouTube/Stripe/etc. chatter silently
      if (!data || typeof data !== "object") return;
      console.log("[embed-shop] message from allowed origin", { origin: event.origin, type: data.type });
      if (data.type === "fender-embed:shop") {
        // Resolve any relative URLs (e.g. Contentful's "/collections/…") against the host's
        // origin — the message came from an allowlisted fender origin, so that's where its
        // storefront links belong.
        const content = this._sanitize(data, event.origin);
        console.log("[embed-shop] accepted shop payload", { products: content.products.length, heading: content.heading });
        // We've been heard — stop pinging.
        this._stopReadyPings();
        // postMessage handlers run OUTSIDE Angular's zone — re-enter it so the shop panel
        // re-renders (otherwise the data arrives but change detection never runs).
        this.zone.run(() => this._shop$.next(content));
      }
    };
    window.addEventListener("message", this._listener);
    console.log("[embed-shop] announcing ready (will re-ping until the host answers)");
    // Re-announce ready until the host answers with a shop payload. The host's message listener
    // may attach well after our first ping (Contentful/marketing pages inject scripts late), so a
    // single ping — or even a short burst — is unreliable. Keep pinging until we get a shop payload
    // (cleared in the listener) or the component tears down (stop()).
    this._announceReady();
    this._readyInterval = setInterval(() => this._announceReady(), 800);
  }

  private _stopReadyPings() {
    if (this._readyInterval) {
      clearInterval(this._readyInterval);
      this._readyInterval = null;
    }
  }

  stop() {
    this._stopReadyPings();
    if (this._listener) {
      window.removeEventListener("message", this._listener);
      this._listener = null;
    }
  }

  /**
   * Load the shopping rail straight from a URL param — for host pages that can't run any JS
   * (e.g. a Contentful field that renders via innerHTML, so an inline <script> never executes).
   * The host pre-encodes the payload and bakes it into the iframe src: `?shop=<base64url(JSON)>`.
   * The JSON is either the full payload `{heading, blurb, shopAllUrl, products}` or just the
   * `products` array. Same sanitization as the postMessage path (https-only URLs, data not markup).
   */
  loadFromEncoded(encoded: string) {
    if (!encoded) return;
    try {
      let b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
      while (b64.length % 4) b64 += "=";
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      const raw = JSON.parse(new TextDecoder().decode(bytes));
      const content = this._sanitize(Array.isArray(raw) ? { products: raw } : raw);
      console.log("[embed-shop] loaded shop from URL param", { products: content.products.length });
      this._shop$.next(content);
    } catch (e) {
      console.warn("[embed-shop] failed to decode ?shop= param", e);
    }
  }

  /**
   * DEMO SCAFFOLDING (remove once the host-page plumbing is live): seed the rail with sample
   * products so the Shop UI can be styled/pruned without the parent postMessage. Triggered by
   * `?shopDemo=true` on the embed URL.
   */
  seedDemo() {
    this._shop$.next({
      heading: "Shop This Livestream",
      blurb: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec viverra ornare leo non tincidunt.",
      shopAllUrl: "https://www.fender.com/",
      products: [
        {
          title: "American Professional II Stratocaster®",
          price: "$1,749.99",
          colors: "15 Colors",
          image: "https://www.fender.com/cdn-cgi/image/format=auto,resize=height=auto,width=850,rotate=270/https://www.fmicassets.com/Damroot/eCommPNG/10001/0113900700_fen_ins_frt_1_rr.png",
          url: "https://www.fender.com/",
        },
        {
          title: "American Professional II Telecaster®",
          price: "$1,749.99",
          colors: "9 Colors",
          image: "https://www.fender.com/cdn-cgi/image/format=auto,resize=height=auto,width=1500,rotate=270/https://www.fmicassets.com/Damroot/eCommPNG/10128/0177011728_fen_ins_frt_1_rr.png",
          url: "https://www.fender.com/",
        },
        {
          title: "Player II Jazz Bass®",
          price: "$849.99",
          colors: "6 Colors",
          image: "https://www.fender.com/cdn-cgi/image/format=auto,resize=height=auto,width=1500,rotate=270/https://www.fmicassets.com/Damroot/eCommPNG/10121/0140480530_fen_ins_frt_1_rr.png",
          url: "https://www.fender.com/",
        },
        {
          title: "Vintera® III Mid '60s Jazzmaster®",
          price: "$1,349.99",
          colors: "3 Colors",
          image: "https://www.fender.com/cdn-cgi/image/format=auto,resize=height=auto,width=1500,rotate=270/https://www.fmicassets.com/Damroot/eCommPNG/10122/0149290305_fen_ins_frt_1_rr.png",
          url: "https://www.fender.com/",
        },
      ],
    });
  }

  private _announceReady() {
    try {
      if (window.parent && window.parent !== window) {
        // "ready" carries no data — broadcast to any parent and let the host's own origin check
        // gate it. (Deriving the parent origin from document.referrer is unreliable in an SPA.)
        window.parent.postMessage({ type: "fender-embed:ready" }, "*");
      } else {
        console.log("[embed-shop] no parent frame — not in an iframe?");
      }
    } catch (e) {
      console.warn("[embed-shop] postReady failed", e);
    }
  }

  private _allowedOrigin(origin: string): boolean {
    try {
      const host = new URL(origin).hostname;
      return /(^|\.)fender\.com$/i.test(host) || /(^|\.)fendergarage\.com$/i.test(host) || host === "localhost" || host === "127.0.0.1";
    } catch (e) {
      return false;
    }
  }

  private _sanitize(data: any, baseOrigin?: string): EmbedShopContent {
    const rawProducts = Array.isArray(data.products) ? data.products : [];
    const products: EmbedShopProduct[] = rawProducts
      .slice(0, 24)
      .map((p: any) => ({
        title: this._str(p?.title),
        price: this._str(p?.price),
        colors: this._str(p?.colors),
        image: this._url(p?.image, baseOrigin),
        url: this._url(p?.url, baseOrigin),
      }))
      .filter((p: EmbedShopProduct) => !!(p.title || p.image));
    return {
      heading: this._str(data.heading) || "Shop",
      blurb: this._str(data.blurb),
      shopAllUrl: this._url(data.shopAllUrl, baseOrigin),
      products,
    };
  }

  private _str(v: any): string {
    return typeof v === "string" ? v.slice(0, 300) : "";
  }

  /**
   * Resolve a URL to an absolute https one, or drop it. Absolute https URLs pass through;
   * relative URLs (e.g. Contentful's "/collections/…") are resolved against the host origin so
   * they point at the host's storefront — a bare relative href inside the iframe would otherwise
   * resolve against my.fender.com even with target="_top". Anything that doesn't end up https
   * (javascript:/data:/http and other injection vectors) is dropped.
   */
  private _url(v: any, baseOrigin?: string): string {
    const s = typeof v === "string" ? v.trim() : "";
    if (!s) return "";
    try {
      const resolved = new URL(s, baseOrigin || "https://www.fender.com");
      return resolved.protocol === "https:" ? resolved.href : "";
    } catch (e) {
      return "";
    }
  }
}
